
import React from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { Eye, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useOptimizedWarehouseProcurements } from '@/hooks/use-optimized-warehouse-procurements';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

interface OptimizedWarehousePendingListProps {
  onCountChange?: (count: number) => void;
}

const OptimizedWarehousePendingList: React.FC<OptimizedWarehousePendingListProps> = ({
  onCountChange
}) => {
  const {
    procurements,
    isLoading,
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    setSearchTerm,
    goToPage,
    hasNextPage,
    hasPreviousPage
  } = useOptimizedWarehouseProcurements({ status: 'pending' });

  const navigate = useNavigate();

  // Notify parent of count changes
  React.useEffect(() => {
    if (onCountChange && !isLoading) {
      onCountChange(totalItems);
    }
  }, [totalItems, isLoading, onCountChange]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
        <div className="text-sm text-muted-foreground mb-4">
          <Skeleton className="h-4 w-48" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  const getVisiblePages = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by product name or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Results info with total count badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">
          Showing {procurements.length} of {totalItems} pending procurements 
          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </div>
        <Badge variant="secondary" className="ml-2">
          Total: {totalItems}
        </Badge>
      </div>

      {procurements.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            {searchTerm 
              ? "No procurements found matching your search." 
              : "No procurements pending warehouse review."}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {procurements.map((procurement) => (
              <Card key={procurement.id}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{procurement.product_name || 'N/A'}</h3>
                        <Badge variant="outline">{procurement.status}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Quantity:</span> {procurement.total_quantity || 0} kg
                        </div>
                        <div>
                          <span className="font-medium">Amount:</span> Br{procurement.total_amount || 0}
                        </div>
                        <div>
                          <span className="font-medium">Source:</span> {procurement.source_name || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Created:</span> {format(new Date(procurement.created_at), 'PP p')}
                        </div>
                      </div>
                      {procurement.notes && (
                        <div className="text-sm">
                          <span className="font-medium">Notes:</span> {procurement.notes}
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/warehouse/inspect/${procurement.id}`)}
                        className="gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Inspect
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Enhanced Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => goToPage(currentPage - 1)}
                      className={!hasPreviousPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {getVisiblePages().map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => goToPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => goToPage(currentPage + 1)}
                      className={!hasNextPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OptimizedWarehousePendingList;
